import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { COLORS } from '../utils/constants';
import { Mail, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import { userAPI } from '../services/api';
import { useLanguage } from '../context/LanguageContext';

const ForgotPasswordPage = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await userAPI.requestPasswordReset({ email });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка при отправке запроса');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: COLORS.background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          width: '100%',
          maxWidth: '440px',
          backgroundColor: COLORS.card,
          borderRadius: '16px',
          padding: '40px',
          textAlign: 'center'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: `${COLORS.success}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <CheckCircle size={40} color={COLORS.success} />
          </div>
          
          <h1 style={{ color: COLORS.text, fontSize: '24px', marginBottom: '12px' }}>
            Письмо отправлено
          </h1>
          
          <p style={{ color: COLORS.textSecondary, marginBottom: '24px', lineHeight: '1.6' }}>
            Если аккаунт с email <strong style={{ color: COLORS.text }}>{email}</strong> существует, 
            вы получите письмо с инструкциями по восстановлению пароля.
          </p>
          
          <p style={{ color: COLORS.textSecondary, fontSize: '14px', marginBottom: '32px' }}>
            Не получили письмо? Проверьте папку «Спам» или попробуйте снова через несколько минут.
          </p>

          <Link
            to="/login"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              color: COLORS.primary,
              textDecoration: 'none',
              fontSize: '15px',
              fontWeight: '500'
            }}
          >
            <ArrowLeft size={18} />
            Вернуться к входу
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: COLORS.background,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '440px'
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <h1 style={{ 
            fontSize: '32px', 
            fontWeight: '700',
            marginBottom: '8px'
          }}>
            <span style={{ color: COLORS.primary }}>Ex</span>
            <span style={{ color: COLORS.text }}>Dex</span>
          </h1>
          <p style={{ color: COLORS.textSecondary, fontSize: '14px' }}>
            Decentralized Cryptocurrency Exchange
          </p>
        </div>

        {/* Form Card */}
        <div style={{
          backgroundColor: COLORS.card,
          borderRadius: '16px',
          padding: '32px'
        }}>
          <h2 style={{ 
            color: COLORS.text, 
            fontSize: '22px', 
            fontWeight: '600',
            marginBottom: '8px'
          }}>
            Восстановление пароля
          </h2>
          
          <p style={{ 
            color: COLORS.textSecondary, 
            fontSize: '14px',
            marginBottom: '24px',
            lineHeight: '1.5'
          }}>
            Введите email, указанный при регистрации. Мы отправим вам ссылку для создания нового пароля.
          </p>

          {error && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '12px 16px',
              backgroundColor: `${COLORS.danger}15`,
              border: `1px solid ${COLORS.danger}30`,
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <AlertCircle size={18} color={COLORS.danger} />
              <span style={{ color: COLORS.danger, fontSize: '14px' }}>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                color: COLORS.textSecondary, 
                fontSize: '14px',
                marginBottom: '8px'
              }}>
                Email адрес
              </label>
              <div style={{ position: 'relative' }}>
                <Mail 
                  size={18} 
                  color={COLORS.textSecondary} 
                  style={{ 
                    position: 'absolute', 
                    left: '14px', 
                    top: '50%', 
                    transform: 'translateY(-50%)' 
                  }} 
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Введите email"
                  required
                  style={{
                    width: '100%',
                    padding: '14px 14px 14px 44px',
                    backgroundColor: COLORS.background,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '10px',
                    color: COLORS.text,
                    fontSize: '15px',
                    outline: 'none'
                  }}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !email}
              style={{
                width: '100%',
                padding: '16px',
                backgroundColor: loading || !email ? COLORS.border : COLORS.primary,
                color: loading || !email ? COLORS.textSecondary : '#000',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading || !email ? 'not-allowed' : 'pointer',
                marginBottom: '20px'
              }}
            >
              {loading ? 'Отправка...' : 'Отправить ссылку'}
            </button>
          </form>

          <div style={{ textAlign: 'center' }}>
            <Link
              to="/login"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                color: COLORS.textSecondary,
                textDecoration: 'none',
                fontSize: '14px'
              }}
            >
              <ArrowLeft size={16} />
              Вернуться к входу
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
